/**
 * Resuming an interrupted local run — **ADR-067 (1)**, IA 39's checkpointing half.
 *
 * ⚠️ **The mechanism was specified, the `stage` column existed with the ADR's own note on it, and
 * nothing wrote it or read it.** `persistRun` wrote `complete` before a single pair had landed, so
 * there was no such thing as an interrupted run: a tab closed mid-write left a run the whole app
 * believed had finished with nothing behind it — every screen reporting zero duplicates on a file
 * that has them. This is the "who imports this?" defect on a column rather than a function.
 *
 * ── What "resume" honestly means for the shipped engine ───────────────────────────────────────
 *
 * ADR-067 (1) makes resumption **stage-granular, deliberately not finer**: intra-stage checkpointing
 * was rejected because it would fork the wasm32 target from the container, and
 * [11 §1.2](../../../../docs/platform/11-technical-architecture.md)'s *stateless per job* contract
 * already makes a stage a job. So the unit here is a stage, and there are two that can be
 * interrupted:
 *
 *   `ingesting`  the file was parsed; records may or may not have landed.
 *   `matching`   the records ARE in the store; the pairs and entities are not (or are partial).
 *
 * ⚠️ **Only `matching` is resumable, and that asymmetry is the honest one.** Its input — the records
 * — is durable, so the work can be redone from what the device already holds. An `ingesting` run's
 * input is the *file*, which the browser cannot re-read without the person choosing it again; a
 * "resume" that silently needed a file picker would be a button that does not do what it says.
 *
 * ⚠️ **Resuming re-runs the stage rather than continuing inside it.** Matching is deterministic over
 * the same records, so a repeat produces the same pairs — which is what makes discarding a partial
 * write and starting the stage again *safe*, and what makes a half-finished stage worth nothing.
 */
import { openLocalStore, type LocalStore, type StoredPair, type StoredEntity, type StoredRun } from './store.js';
import type { MatchRecord, MatchResult } from '@dedupr/match-core';

export interface ResumableRun {
  run: StoredRun;
  /** How many records the stage would re-match. `0` means there is nothing to resume with. */
  records: number;
  /**
   * Whether this run can actually be resumed.
   *
   * ⚠️ `false` with a reason rather than hiding the run: a person who closed a tab mid-upload should
   * be told the file has to be chosen again, not left wondering where their records went.
   */
  resumable: boolean;
  reason: 'ready' | 'needs_the_file_again' | 'no_records';
}

/** Find an interrupted run, if there is one. ⚠️ `null` is the common case and stays cheap. */
export async function findResumable(store: LocalStore): Promise<ResumableRun | null> {
  const run = await store.unfinishedRun();
  if (run === null) return null;
  const records = await store.allRecords();
  if (run.stage === 'ingesting') {
    return { run, records: records.length, resumable: false, reason: 'needs_the_file_again' };
  }
  if (records.length === 0) {
    /* A `matching` run with no records is a store that lost its input — resuming would produce an
       empty result and present it as a finished match, which is the smaller answer presented as a
       complete one that this repo keeps recording. */
    return { run, records: 0, resumable: false, reason: 'no_records' };
  }
  return { run, records: records.length, resumable: true, reason: 'ready' };
}

export interface ResumeOutcome {
  runId: string;
  pairs: number;
  entities: number;
}

/**
 * Re-run the match stage for an interrupted run and complete it.
 *
 * `match` is injected — the caller owns the Worker, and this module owns the store transitions. That
 * keeps the resume logic testable without a Worker, which is the same seam `persistRun` uses.
 */
export async function resumeRun(
  runId: string,
  match: (records: MatchRecord[]) => Promise<MatchResult>
): Promise<ResumeOutcome> {
  const store = await openLocalStore();
  try {
    const records = await store.allRecords();
    if (records.length === 0) throw new Error('There are no records on this device to match.');

    const sourceOf = new Map(records.map((r) => [r.id, r.source_id]));
    let result: MatchResult;
    try {
      result = await match(records as unknown as MatchRecord[]);
    } catch (error) {
      /* ⚠️ **A failed resume is recorded as `failed`, not left `matching`.** Left open, the next
         launch would offer to resume it again — an infinite invitation to re-run the thing that
         breaks. `failed` is terminal, so the offer stops and the reason is on the row. */
      await store.finishRun(runId, {
        stage: 'failed',
        finished_at: new Date().toISOString(),
        error: error instanceof Error ? error.message : 'The match failed.',
      });
      throw error;
    }

    const pairs: StoredPair[] = result.pairs.map((p, i) => ({
      ...p,
      id: `${runId}:p${i}`,
      run_id: runId,
      cross_source: sourceOf.get(p.record_a_id) !== sourceOf.get(p.record_b_id),
    })) as StoredPair[];
    const entities: StoredEntity[] = result.entities.map((e) => ({
      ...e,
      id: `${runId}:${e.id}`,
      run_id: runId,
    })) as StoredEntity[];

    /* ⚠️ Ids are derived from the run id and the index, exactly as `persistRun` derives them — so a
       resumed stage OVERWRITES whatever the interrupted one had half-written rather than appending
       beside it. Fresh ids would leave a partial set of orphans that every count then included. */
    await store.saveResult(runId, pairs, entities);
    await store.finishRun(runId, { stage: 'complete', finished_at: new Date().toISOString(), stats: result.stats });
    return { runId, pairs: pairs.length, entities: entities.length };
  } finally {
    store.close();
  }
}

/**
 * Abandon an interrupted run.
 *
 * ⚠️ **Marks it `failed`; it does not delete it.** The run is the only record that the work happened
 * at all, and a person who chooses not to resume has still had an interrupted session — deleting the
 * row would make the audit trail's own account of the device incomplete for the sake of tidiness.
 */
export async function abandonRun(runId: string): Promise<void> {
  const store = await openLocalStore();
  try {
    await store.finishRun(runId, {
      stage: 'failed',
      finished_at: new Date().toISOString(),
      error: 'Abandoned — this run was interrupted and not resumed.',
    });
  } finally {
    store.close();
  }
}
